import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { PLANS, PLAN_IDS, type PlanId } from '@ready/shared';
import { useApi, type AdminOrgRequest, type OrgRequestStatus } from '@/lib/api';
import { ORG_TYPE_LABELS } from '@/lib/labels';
import { Badge, Button, Card, ErrorText, Field, Input, Select, Textarea } from '@/components/ui';

const FILTERS: Array<OrgRequestStatus | 'all'> = [
  'pending',
  'approved',
  'rejected',
  'canceled',
  'all',
];

export function AdminRequestsPage() {
  const api = useApi();
  const [status, setStatus] = useState<OrgRequestStatus | 'all'>('pending');
  const requests = useQuery({
    queryKey: ['admin', 'requests', status],
    queryFn: () => api.admin.orgRequests(status),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f}
            variant={f === status ? 'primary' : 'secondary'}
            onClick={() => setStatus(f)}
          >
            {f[0]!.toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>
      {requests.isError && <ErrorText error={requests.error} />}
      {requests.data?.requests.length === 0 && (
        <p className="text-sm text-muted-foreground">No requests.</p>
      )}
      {requests.data?.requests.map((r) => (
        <RequestCard key={r.id} r={r} />
      ))}
    </div>
  );
}

function RequestCard({ r }: { r: AdminOrgRequest }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [planId, setPlanId] = useState<PlanId>('pilot');
  const [maxMembers, setMaxMembers] = useState('');
  const [note, setNote] = useState('');
  const done = () => queryClient.invalidateQueries({ queryKey: ['admin'] });

  const approve = useMutation({
    mutationFn: () =>
      api.admin.approve(r.id, {
        planId,
        note: note || undefined,
        limitOverrides: maxMembers ? { maxMembers: Number(maxMembers) } : undefined,
      }),
    onSuccess: done,
  });
  const reject = useMutation({ mutationFn: () => api.admin.reject(r.id, note), onSuccess: done });

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">{r.name}</h2>
          <p className="text-sm text-muted-foreground">
            {ORG_TYPE_LABELS[r.type]} · {r.country} · ~{r.expectedMembers.toLocaleString()} members
            · from {r.requesterEmail}
          </p>
        </div>
        <Badge>{r.status}</Badge>
      </div>

      <dl className="mt-3 grid gap-1 text-sm sm:grid-cols-2">
        {r.website && (
          <div>
            <dt className="inline text-muted-foreground">Website: </dt>
            <dd className="inline break-all">{r.website}</dd>
          </div>
        )}
        {r.domain && (
          <div>
            <dt className="inline text-muted-foreground">Domain: </dt>
            <dd className="inline">{r.domain}</dd>
          </div>
        )}
      </dl>
      {r.message && <p className="mt-2 text-sm">“{r.message}”</p>}

      <ul className="mt-3 space-y-1 text-sm">
        {r.domain && (
          <li className="flex items-center gap-2">
            {r.signals.emailMatchesDomain ? (
              <CheckCircle2 className="size-4 text-india-green" aria-hidden />
            ) : (
              <AlertTriangle className="size-4 text-saffron" aria-hidden />
            )}
            {r.signals.emailMatchesDomain
              ? 'Requester email matches the domain'
              : "Requester email doesn't match the domain"}
          </li>
        )}
        {r.signals.domainTakenBy && (
          <li className="flex items-center gap-2">
            <AlertTriangle className="size-4 text-destructive" aria-hidden />
            Domain already verified by {r.signals.domainTakenBy}
          </li>
        )}
      </ul>

      {r.status === 'pending' ? (
        <div className="mt-4 grid gap-4 border-t border-border pt-4 sm:grid-cols-[12rem_10rem_1fr]">
          <Field label="Plan" htmlFor={`plan-${r.id}`}>
            <Select
              id={`plan-${r.id}`}
              value={planId}
              onChange={(e) => setPlanId(e.target.value as PlanId)}
            >
              {PLAN_IDS.map((p) => (
                <option key={p} value={p}>
                  {PLANS[p].name} ({PLANS[p].limits.maxMembers.toLocaleString()} members)
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Members override" htmlFor={`members-${r.id}`}>
            <Input
              id={`members-${r.id}`}
              type="number"
              min={1}
              placeholder="Plan default"
              value={maxMembers}
              onChange={(e) => setMaxMembers(e.target.value)}
            />
          </Field>
          <Field label="Note to requester" htmlFor={`note-${r.id}`} hint="Required when rejecting">
            <Textarea
              id={`note-${r.id}`}
              rows={1}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <div className="flex gap-3 sm:col-span-3">
            <Button
              disabled={approve.isPending || reject.isPending}
              onClick={() => approve.mutate()}
            >
              Approve and create
            </Button>
            <Button
              variant="secondary"
              disabled={!note || approve.isPending || reject.isPending}
              onClick={() => reject.mutate()}
            >
              Reject
            </Button>
            <ErrorText error={approve.error ?? reject.error} />
          </div>
        </div>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          {r.decisionNote && <>Note: {r.decisionNote} </>}
          {r.orgId && (
            <Link to={`/organizations/${r.orgId}`} className="text-primary">
              Open organization
            </Link>
          )}
        </p>
      )}
    </Card>
  );
}
